import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { Skeleton } from '@design-system/Skeleton';

import { useOrderDetailViewModel } from '@/features/OrderTrackingViewModels';
import { analyticsEvent } from '@/utils/analyticsEvent';
import { EventTracker } from '@/utils';
import type { AddressInfo } from '@viome/viome-base/src/usecase/OrderTracking/OrderTracking.model';
import { NavBar } from '@design-system/NavBar.tsx';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (!amount) return '—';
  return `$${amount.toFixed(2)}`;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="typography-h2 text-primary px-1">{children}</h2>
);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <ListItem hideIndicator>
    <span className="typography-body text-primary flex-1">{label}</span>
    <span className="typography-caption1 text-tertiary">{value}</span>
  </ListItem>
);

const AddressBlock = ({
  address,
  testIdPrefix,
}: {
  address: AddressInfo | null;
  testIdPrefix: string;
}) => {
  if (!address) return <p className="text-tertiary">—</p>;

  return (
    <div
      className="flex flex-col typography-body text-tertiary"
      data-testid={`${testIdPrefix}-address`}
    >
      <span>{address.full_name}</span>
      <span>{address.address_line1}</span>
      {address.address_line2 && <span>{address.address_line2}</span>}
      <span>
        {address.city}, {address.state} {address.zip}
      </span>
      <span>{address.country}</span>
    </div>
  );
};

export const OrderDetailPage = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderDetails, isOrderDetailsLoading, isOrderDetailsError } =
    useOrderDetailViewModel(orderNumber ?? '');

  useEffect(() => {
    EventTracker.trackEvent(
      'account',
      analyticsEvent.ORDER_TRACKING_ORDER_DETAIL_VIEW,
    );
  }, []);

  if (isOrderDetailsLoading) {
    return (
      <section
        className="page-container flex flex-col gap-10 pb-24"
        data-testid="orderDetail-skeleton"
      >
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isOrderDetailsError || !orderDetails) {
    return (
      <section
        className="page-container flex flex-col gap-12 pb-24"
        data-testid="orderDetail-empty"
      >
        <div className="flex flex-col gap-6">
          <NavBar returnButtonTitle="Back" className="md:-ml-9" />
          <h1
            className="typography-h6 text-primary"
            data-testid="orderDetail-title"
          >
            Order Details
          </h1>
        </div>
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card>
            <CardContent className="py-5 px-5">
              <span
                className="typography-body text-secondary"
                data-testid="orderDetail-emptyMessage"
              >
                No order details available at this time.
              </span>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  const {
    shipping_information,
    order_information,
    payment_information,
    transaction_details,
  } = orderDetails;

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="orderDetail-page"
    >
      <div className="flex flex-col gap-6" data-testid="orderDetail-header">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <h1
          className="typography-h6 text-primary"
          data-testid="orderDetail-title"
        >
          Order Details
        </h1>
      </div>

      {shipping_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Shipping Information</SectionTitle>
          <Card data-testid="orderDetail-shippingCard">
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span
                  className="typography-body text-primary"
                  data-testid="orderDetail-shippingTitle"
                >
                  Shipping Address
                </span>
                <AddressBlock
                  address={shipping_information}
                  testIdPrefix="orderDetail-shipping"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {order_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card data-testid="orderDetail-orderInfoCard">
            <CardContent className="p-0">
              <List data-testid="orderDetail-orderInfoList">
                <InfoRow
                  label="Date"
                  value={formatDate(order_information.order_date)}
                />
                <InfoRow
                  label="Order Number"
                  value={`#${order_information.order_number}`}
                />
                <InfoRow
                  label="Total"
                  value={formatCurrency(order_information?.order_total)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}

      {payment_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Payment Information</SectionTitle>
          <Card data-testid="orderDetail-paymentCard">
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span
                  className="typography-body text-primary"
                  data-testid="orderDetail-paymentTitle"
                >
                  Billing Address
                </span>
                <AddressBlock
                  address={payment_information}
                  testIdPrefix="orderDetail-payment"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {transaction_details && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Transaction Details</SectionTitle>
          <Card data-testid="orderDetail-transactionCard">
            <CardContent className="p-0">
              <List data-testid="orderDetail-transactionList">
                <InfoRow
                  label="Price"
                  value={formatCurrency(transaction_details?.overall_price)}
                />
                <InfoRow
                  label="Tax"
                  value={formatCurrency(transaction_details?.total_tax)}
                />
                {transaction_details?.total_discounts != null &&
                  transaction_details.total_discounts > 0 && (
                    <InfoRow
                      label="Discount"
                      value={`-${formatCurrency(transaction_details.total_discounts)}`}
                    />
                  )}
                <InfoRow
                  label="Order Total"
                  value={formatCurrency(transaction_details?.final_price)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
};
